import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { NotificationService } from 'app/_services/notification.service';
import { SiteContactsService } from 'app/_services/site-contacts.service';
import { SiteService } from 'app/_services/site.service';
import { environment } from 'environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-site-contacts',
  templateUrl: './site-contact-info.component.html',
})
export class SiteContactsComponent implements OnInit {
  public selectedSite: any;
  private selectedSiteId: any;
  public siteContacts: any = [];

  public pageSizes: any = environment.pageSizes;
  public pagination: any;

  public isAddContactModalShown = false;
  public addSiteContactForm: FormGroup;
  public errorAlerts = [];
  public isLoading = false;

  private isPhoneNumberValid = false;

  constructor(
    private siteService: SiteService,
    private siteContactsService: SiteContactsService,
    public notificationService: NotificationService,
    activatedRoute: ActivatedRoute,
    fb: FormBuilder,
  ) {
    activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
    });

    this.resetPagination();

    this.addSiteContactForm = fb.group({
      contactName: new FormControl('', [Validators.required]),
      email: new FormControl(''),
      phoneNumber: new FormControl(''),
      countryCode: new FormControl(''),
      enabled: new FormControl(true),
      contactType: new FormControl('', [Validators.required]),
    }, {
      validator: (formControl) => {
        if (!formControl.controls.email.value
          && !formControl.controls.phoneNumber.value) {
            return {invalid: true};
        }
      }
    });

  }

  ngOnInit() {
    this.getSelectedSite(this.selectedSiteId);
    this.getAllSiteContacts();
  }

  public getSelectedSite(id) {
    this.siteService.getSiteById(id).subscribe((data: any) => {
      this.selectedSite = data;
    });
  }

  public getAllSiteContacts() {
    this.siteContactsService.getAllSiteContactsBySiteId(this.selectedSiteId, this.pagination).subscribe((data: any) => {
      this.siteContacts = data.body;
      this.pagination.totalRecords = Number(data.headers.get('X-Total-Count'));
    });
  }

  //
  // Table actions
  //

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getAllSiteContacts();
  }

  public resetPagination() {
    this.pagination = {
      size: this.pageSizes[1],
      page: 0,
      totalRecords: 0,
    };
  }

  public deleteContact(selectedContact) {
    Swal.fire({
      title: 'Delete Contact?',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.siteContactsService.deleteSiteContact(selectedContact.id).subscribe(() => {
          this.getAllSiteContacts();
          this.notificationService.notify('success', 'Deleted', 'Contact has been removed');
        });
      }
    });
  }

  public onIntlCodeChanged(phoneNumberData) {
    this.isPhoneNumberValid = phoneNumberData.valid;

    this.addSiteContactForm.get('phoneNumber').setValue(phoneNumberData.phoneNumber);
    this.addSiteContactForm.get('countryCode').setValue(phoneNumberData.dialCode);
  }

  //
  // Add a new contact
  public showAddContactModal() {
    this.addSiteContactForm.reset();
    this.addSiteContactForm.patchValue({
      contactType: 'ADMIN',
      enabled: true,
    });
    this.errorAlerts = [];
    this.isAddContactModalShown = true;
  }

  public async onAddSiteContactFormSubmit() {
    this.isLoading = true;
    this.errorAlerts = [];

    const values = this.addSiteContactForm.value;
    const email = values.email ? values.email.trim() : '';
    const phoneNumber = values.phoneNumber ? values.phoneNumber.trim() : '';
    const countryCode = values.countryCode ? values.countryCode.trim() : '';
    const enabled = values.enabled;
    const emergencyContact = values.contactType === "EMERGENCY";

    if (phoneNumber && !this.isPhoneNumberValid) {
      // Not sure how to correct the z-index of the Swal alert inside the modal, so hiding the modal instead
      this.isAddContactModalShown = false;
      const shouldContinue = await this.doubleCheckSavingInvalidPhoneNumber(phoneNumber, countryCode);
      this.isAddContactModalShown = true;

      if (!shouldContinue) {
        this.isLoading = false;
        return;
      }
    }

    const body = {
      contactName: values.contactName,
      email: email,
      phoneNumber: phoneNumber,
      countryCode: phoneNumber ? countryCode : '',
      siteId: this.selectedSiteId,
      enabled: enabled,
      emergencyContact: emergencyContact,
    };

    this.siteContactsService.createSiteContact(body).subscribe(() => {
      this.isLoading = false;
      this.isAddContactModalShown = false;

      this.getAllSiteContacts();
      this.notificationService.notify('success', 'Success', 'New Site Contact Added');

    }, (error) => {
      if (error.error && error.error.title && error.error.title === 'Method argument not valid') {
        console.error(error);
        if (error.error.fieldErrors[0].field === 'email') {
          this.errorAlerts.push({ severity: 'warn', summary: 'The email address entered is not valid'});
        }
      } else {
        throw error;
      }
    });
  }

  private doubleCheckSavingInvalidPhoneNumber(phoneNumber, countryCode): Promise<boolean> {
    return new Promise((resolve, reject) => {
      return Swal.fire({
        title: `${countryCode} ${phoneNumber}`,
        text: `We've detected this phone number might not be valid. Do you want to save it anyway?`,
        type: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'confirm-primary',
        },
        confirmButtonText: 'Yes, save anyway',
      }).then((result) => {
        if (result.value) {
          return resolve(true);
        } else {
          return resolve(false);
        }
      });
    })
  }

  public updateSiteContactsNotification(contact: any) {

    if (!contact) {
      return;
    }

    const previousNotificationState = !contact.enabled;
    const body = {
      id: contact.id,
      contactName: contact.contactName,
      email: contact.email,
      phoneNumber: contact.phoneNumber,
      countryCode: contact.countryCode,
      emergencyContact: contact.emergencyContact,
      enabled: contact.enabled,
    };
    this.siteContactsService.updateSiteContact(body).subscribe(
      (response) => {
        this.notificationService.notify('success', 'Success', 'Site Contact Notification Enabled Updated');
      }, (error) => {
        contact.enabled = previousNotificationState;
        this.notificationService.notify('danger', 'Error', 'Error Updating Site Contact Notification Enabled');
        console.error(error);
      });
  }

}
